import { Theme, css, keyframes } from '@emotion/react';

import NextImage, { ImageProps as NextImageProps } from 'next/image';

import React from 'react';

import { FontSizes, Spacing } from 'chatie-design-system';

import { IEFinderObject } from '../../model';
import { resolveEFinderImageSource } from '../../utils/efinderImage';
import { CSSProps } from '../../utils/misc';

type Props = {
  image?: IEFinderObject<'image'>;
  h?: number;
  radius?: string | number;
  children?: React.ReactNode;
  showLoadingState?: boolean;
  fallbackText?: string;
};

type ImageProps = Pick<
  NextImageProps,
  | 'width'
  | 'height'
  | 'layout'
  | 'objectFit'
  | 'objectPosition'
  | 'alt'
  | 'onClick'
  | 'loading'
  | 'priority'
  | 'onLoadingComplete'
  | 'onError'
  | 'crossOrigin'
>;

const fadeInKeyframes = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const imageCSS = (radius?: string | number) => css`
  animation: ${fadeInKeyframes} 0.5s linear forwards;
  overflow: hidden;
  border-radius: inherit;
  ${radius &&
  css`
    border-radius: ${typeof radius === 'string' ? radius : `${radius}px`};
  `}
`;

const imageFrameCSS = (theme: Theme) => css`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${theme.var.gray100};

  &[data-state='loading'] img,
  &[data-state='error'] img {
    opacity: 0;
  }

  .image-state {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${Spacing.xsmall}px;
    color: ${theme.var.gray400};
    text-align: center;
    font-size: ${FontSizes.xsmall}px;
    line-height: 1.35;
    background: ${theme.var.gray100};
  }

  .image-state[data-loading='true']::after {
    width: 28px;
    height: 28px;
    border: 3px solid ${theme.colors.main100};
    border-top-color: ${theme.colors.main500};
    border-radius: 50%;
    content: '';
    animation: efinder-image-spin 1s linear infinite;
  }

  @keyframes efinder-image-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ratio = 2;

const EFinderImage = ({
  className,
  image,
  h,
  radius,
  width,
  height,
  alt,
  objectFit,
  objectPosition,
  loading,
  layout,
  priority,
  onLoadingComplete,
  onError,
  showLoadingState = false,
  fallbackText = '이미지를 불러오지 못했어요',
  ...rest
}: CSSProps & Props & ImageProps) => {
  const attachment = image?.attachment;
  const bgUrl = attachment
    ? resolveEFinderImageSource({
        url: attachment.url,
        contentType: attachment.contentType,
        requestedHeight: h === undefined ? undefined : h * ratio,
        attachmentHeight: attachment.height,
      })
    : '';

  const [loadState, setLoadState] = React.useState<
    'loading' | 'loaded' | 'error'
  >('loading');

  React.useEffect(() => {
    setLoadState('loading');
  }, [bgUrl]);

  if (!attachment) {
    return null;
  }

  const renderedImage = (
    <NextImage
      css={imageCSS(radius)}
      className={className}
      {...rest}
      src={bgUrl}
      width={width}
      height={height}
      layout={layout || 'fill'}
      loading={loading || (priority ? 'eager' : 'lazy')}
      objectFit={objectFit || 'cover'}
      alt={alt}
      objectPosition={objectPosition || 'center'}
      unoptimized
      priority={!!priority}
      onLoadingComplete={(result) => {
        setLoadState('loaded');
        onLoadingComplete?.(result);
      }}
      onError={(event) => {
        setLoadState('error');
        onError?.(event);
      }}
    />
  );

  if (!showLoadingState) {
    return renderedImage;
  }

  return (
    <div
      css={imageFrameCSS}
      data-state={loadState}
      aria-busy={loadState === 'loading'}
    >
      {renderedImage}
      {loadState === 'loading' && (
        <span
          className="image-state"
          data-loading="true"
          aria-label="이미지 로딩 중"
        />
      )}
      {loadState === 'error' && (
        <span className="image-state">{fallbackText}</span>
      )}
    </div>
  );
};

export const OptimizeEFinderImage = ({
  className,
  image,
  h,
  radius,
  width,
  height,
  alt,
  objectFit,
  objectPosition,
  loading,
  layout,
  priority,
  ...rest
}: CSSProps & Props & ImageProps) => {
  if (!image || !image.attachment) {
    return null;
  }

  const { url, contentType, height: attachmentHeight } = image.attachment;

  const myLoader = ({
    src,
    quality = 75,
  }: {
    src: string;
    quality?: number;
  }) => {
    return `${src}&q=${quality}`;
  };

  const bgUrl = resolveEFinderImageSource({
    url,
    contentType,
    requestedHeight: h === undefined ? undefined : h * ratio,
    attachmentHeight,
  });

  return (
    <NextImage
      css={imageCSS(radius)}
      className={className}
      {...rest}
      src={bgUrl}
      width={width}
      height={height}
      loader={myLoader}
      layout={layout || 'fill'}
      loading={loading || (priority ? 'eager' : 'lazy')}
      objectFit={objectFit || 'cover'}
      alt={alt}
      objectPosition={objectPosition || 'center'}
      priority={!!priority}
    />
  );
};

export default EFinderImage;

export const enum EFinderImageSizes {
  marketItem = 80,
  banner = 420,
  playBanner = 280,
  avatar = 64,
  chatbot = 150,
}
